import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getMessageFromError } from '../../api/axios';
import { useInitHubspotQuote } from '../../api/crmService';
import AppLoading from '../../components/App/AppLoading';
import {
  MAlert,
  MBox,
  MFlex,
  MPageContainer,
  MText,
} from '../../components/Monetize';
import { getAccountQuoteNewRoute, SALES_LANDING } from '../../constants/routes';
import Sentry from '../../services/sentry';

/**
 * Initializes a new quote from a HubSpot deal, or shows an error message.
 * Once the quote is set up, the user is redirected to the new-quote page for the
 * resolved account, and this page is removed from history.
 */
export const CrmHubspotInitQuote = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dealId = searchParams.get('dealId');
  const dealName = searchParams.get('dealName');

  const [errorMessage, setErrorMessage] = useState<string>();

  const { mutate, isPending: isLoading } = useInitHubspotQuote({
    onSuccess: ({ accountId, contactId, fieldMapping, opportunityId }) => {
      const redirectParams = new URLSearchParams({
        skipModal: 'true',
        opportunityId,
        contactId,
      });
      if (fieldMapping && Object.keys(fieldMapping).length > 0) {
        redirectParams.set('fieldMapping', JSON.stringify(fieldMapping));
      }
      navigate(
        `${getAccountQuoteNewRoute(accountId)}?${redirectParams.toString()}`,
        { replace: true },
      );
    },
    onError: (err) => {
      const message = getMessageFromError(
        err,
        'There was a problem setting up your quote',
      );
      setErrorMessage(message);
      Sentry.logger.error('CrmHubspotInitQuote error', {
        dealId,
        dealName,
        error: message,
      });
    },
  });

  useEffect(() => {
    if (dealId && dealName) {
      mutate({ dealId, dealName });
    } else {
      setErrorMessage('Your request is invalid, please start over');
      Sentry.logger.error('CrmHubspotInitQuote missing required parameters', {
        dealId: dealId || '<missing>',
        dealName: dealName || '<missing>',
      });
    }
  }, [dealId, dealName, mutate]);

  return (
    <MPageContainer>
      {isLoading && <AppLoading />}
      {errorMessage && (
        <MAlert type="error" title="Error">
          <MFlex direction="column">
            <MText>{errorMessage}</MText>
            <MBox>
              <Link to={SALES_LANDING}>
                <MText mt={2} textDecor="underline" fontSize="sm">
                  Go to Sales Dashboard
                </MText>
              </Link>
            </MBox>
          </MFlex>
        </MAlert>
      )}
    </MPageContainer>
  );
};
